import {createGlobalStyle} from "styled-components";
import {scrollbarTrack} from "./mixins";
import "react-datepicker/dist/react-datepicker.css";
import {getTheme} from "./themeUtils";

export const GlobalStyles = createGlobalStyle`
    /* ------------------------ */
    /* ----- FONT IMPORTS ----- */
    /* ------------------------ */
    @font-face {
        font-family: "OpenSans";
        src: url("/fonts/open_sans/OpenSans-Regular.ttf");
        font-style: normal;
        font-weight: 400;
        font-display: swap;
    }

    @font-face {
        font-family: "OpenSans";
        src: url("/fonts/open_sans/OpenSans-SemiBold.ttf");
        font-weight: 500;
        font-display: swap;
    }

    @font-face {
        font-family: "OpenSans";
        src: url("/fonts/open_sans/OpenSans-Bold.ttf");
        font-weight: 700;
        font-display: swap;
    }

    html,
    body {
        height: 100%;
        font-size: 16px;
        color: ${getTheme("colors.primary")};
        font-family: "OpenSans", sans-serif;
    }

    /**
   * Chrome has a bug with transitions on load since 2012!
   *
   * To prevent a "pop" of content, you have to disable all transitions until
   * the page is done loading.
   *
   * https://lab.laukstein.com/bug/input
   */
    body.loading * {
        transition: none !important;
    }

    * {
        box-sizing: border-box;
    }

    h1, h2 {
        font-weight: bold;
    }

    h2 {
        margin-bottom: ${getTheme("space.3")};
    }

    a {
        color: inherit;
        text-decoration: none;
    }

    .appContainer {
        width: 90%;
        max-width: ${getTheme("containerWidth")};
        margin-left: auto;
        margin-right: auto;
    }

    hr {
        background-color: ${getTheme("colors.border")};
        opacity: 1;
    }

    input[type="text"],
    input[type="number"],
    input[type="email"],
    input[type="tel"],
    input[type="password"] {
        font-size: 16px;
    }

    .link-bold {
        font-weight: bold;
        color: ${getTheme("colors.link")};
        cursor: pointer;
    }


    /* Components */
    .offcanvas-body {
        .results {
            margin-top: 20px;

            label {
                font-size: ${getTheme("fontSizes.small")};
                color: ${getTheme("colors.secondaryFont")};
            }

            ul {
                padding: 0;

                li {
                    list-style: none;
                    cursor: pointer;
                    margin-bottom: ${getTheme("space.1")};
                    display: flex !important;
                    align-items: center;
                    padding: ${getTheme("space.0")};

                    .img-contain {
                        width: 20%;
                        height: 50px;
                        position: relative;
                        margin-right: ${getTheme("space.2")};
                        display: flex;
                        justify-content: center;
                        align-items: center;

                        img {
                            max-width: 70px;
                            max-height: 100%;

                        }
                    }

                    .product-info {
                        display: flex;
                        flex-direction: column;
                        width: 80%;

                        .product-price {
                            color: ${getTheme("colors.secondaryFont")};
                            font-weight: bold;
                        }
                    }
                }
            }
        }
    }


    /* Overrides */
    .offcanvas {
        border: none !important;
    }

    .carousel-indicators [data-bs-target] {
        width: 10px;
        height: 10px;
        border-radius: 100%;
    }

    #nprogress .bar {
        background: #02bdd0;
    }

    #nprogress .peg {
        box-shadow: 0 0 10px #02bdd0, 0 0 5px #02bdd0;
    }

    .accordion-button:not(.collapsed) {
        background-color: white;
    }

    /* Bootstrap variations */

    // Box tabs
    .box-tabs {
        display: flex;
        justify-content: center;
        border: none;
        padding: 0;

        li.nav-item {
            display: flex;
            justify-content: center;
            border-collapse: collapse;

            .nav-link {
                border: none;
                color: ${getTheme("colors.placeholder")};
            }
        }

        li.nav-item > .nav-link {
            border: 1px solid ${getTheme("colors.border")};
            border-radius: 0;
        }

        // active item

        li.nav-item > .nav-link.active {
            border: 1px solid ${getTheme("colors.primary")};
            color: ${getTheme("colors.primary")};
            width: 100%;

            .nav-link {
                border-bottom: 1px solid ${getTheme("colors.primary")};
                color: ${getTheme("colors.primary")};
            }
        }
    }

    // Flat tabs
    .flat-tabs {
        display: flex;
        justify-content: center;

        .nav-link {
            color: ${getTheme("colors.placeholder")};
            padding: ${getTheme("space.1")} 0;
            margin: 0 ${getTheme("space.1")};
            border: none;

            &:not(.active):hover {
                border-bottom-width: 2px;
            }

            &.active {
                color: ${getTheme("colors.primary")};
                border-bottom: 2px solid ${getTheme("colors.primary")};
            }
        }
    }


    // Flat tabs big
    .flat-tabs-big {
        display: flex;
        justify-content: left;

        .nav-link {
            font-size: ${getTheme("fontSizes.large")};
            font-weight: bold;
            color: ${getTheme("colors.placeholder")};
            padding: ${getTheme("space.1")} 0;
            margin: 0 ${getTheme("space.1")};
            border: none;

            &:not(.active):hover {
                border-bottom-width: 2px;
            }

            &.active {
                color: ${getTheme("colors.primary")};
                border-bottom: 2px solid ${getTheme("colors.primary")};
            }
        }
    }

    .placeholder {
        border-radius: ${getTheme("borderRadius")};
        opacity: 0.2;
    }

    .form-check-input {
        border-color: ${getTheme("colors.border")};
    }

    .form-check-input:checked {
        background-color: ${getTheme("colors.primary")};
        border-color: ${getTheme("colors.primary")};
    }

    .form-check-input[type=radio] {
        border-color: ${getTheme("colors.primary")};
    }

    .form-check-input:checked[type=radio] {
        background-color: white;
        background-image: url('/icons/black-circle.svg');
    }

    .form-check-input:focus[type=radio] {
        border-color: ${getTheme("colors.primary")};
        box-shadow: none;
    }

    .form-check-input:focus {
        box-shadow: none;
    }

    .input-group-text {
        border: 1px solid ${getTheme("colors.border")};
        background-color: white;
    }

    .form-control {
        border: 1px solid ${getTheme("colors.border")};
    }

    .form-control:focus {
        border-color: ${getTheme("colors.border")};
        box-shadow: none;
    }

    .form-switch .form-check-input {
        height: 20px;
        width: 40px;
        margin-top: 2px;
        margin-right: ${getTheme("space.1")};
        background-color: ${getTheme("colors.border")};
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
    }

    .form-switch .form-check-input:focus {
        border-color: ${getTheme("colors.border")};
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
    }

    .form-switch .form-check-input:checked {
        border: none;
        background-color: ${getTheme("colors.textGreen")};
        border-color: ${getTheme("colors.textGreen")};
    }

    .offcanvas-body {
        ${scrollbarTrack}
    }

    /* Datepicker variations */
    .react-datepicker-popper {
        z-index: 10;
    }

    .react-datepicker {
        border: 1px solid ${getTheme("colors.border")};
        box-shadow: 0 0 ${getTheme("space.0")} ${getTheme("colors.border")};
        font-family: "OpenSans", sans-serif;

        &__header {
            border-bottom: 1px solid ${getTheme("colors.border")};
            padding: ${getTheme("space.2")};
            background-color: ${getTheme("colors.background")};
        }

        &__navigation {
            top: ${getTheme("space.2")};
        }

        &-popper[data-placement^=bottom] {
            .react-datepicker__triangle::before {
                border-bottom-color: ${getTheme("colors.border")};
            }

            .react-datepicker__triangle::after {
                border-bottom-color: ${getTheme("colors.background")};
            }
        }
    }

    .react-datepicker-wrapper {
        flex: 1;

        input {
            width: 100%;
            padding: 0.375rem 0.75rem;
            border: 1px solid ${getTheme("colors.border")};
            outline: none;
        }
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--selected:hover {
        background-color: ${getTheme("colors.primary")};
        border-radius: 50%;
    }

    .react-datepicker__day--outside-month {
        color: ${getTheme("colors.secondaryFont")};
    }

    /* Utility Classes */
    .separator {
        border-bottom: 1px solid ${getTheme("colors.border")};
        margin: ${getTheme("space.2")} 0;
    }

    .angled-strikethrough {
        font-size: ${getTheme("fontSizes.small")};
        background: linear-gradient(to left top, transparent 47.75%, currentColor 49.5%, currentColor 50.5%, transparent 52.25%);
    }

    // carousel styles override
    .react-multi-carousel-list {
        padding-bottom: ${getTheme("space.1")};
    }

    .react-multiple-carousel__arrow {
        z-index: 3 !important;
    }

    // benefits carousel
    .specials-carousel .react-multi-carousel-item {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    // react-select
    .react-select {
        margin-left: 0 !important;
    }


    // custom tooltip component
    .custom-tooltip {
        font-size: ${getTheme("fontSizes.small")};

        div {
            &:last-of-type {
                padding: ${getTheme("space.2")};
                line-height: 1.5rem;
            }
        }

        .tooltip-link {
            color: ${getTheme("colors.link")} !important;
            padding-bottom: 0 !important;
        }

        .tooltip-title {
            font-weight: bold;
            margin-bottom: ${getTheme("space.1")};
        }
    }


    // React Phone Input 2
    .react-tel-input {
        z-index: 3;
        margin-top: ${getTheme("space.0")};
        width: 100%;

        input {
            width: 100% !important;
            height: 50px !important;
        }
    }

    .phone-input-error {
        border: 1px solid red !important;
    }


    // Offcanvas
    .offcanvas {
        z-index: 1200
    }

    .offcanvas-backdrop {
        z-index: 1199;
    }


    // Range Slider
    .range-slider {
        margin: ${getTheme("space.2")} 0 !important;
    }


    // Modal
    .modal-body-title {
        font-weight: bold;
        font-size: ${getTheme("fontSizes.large")};
        display: flex;
        justify-content: space-between;
        align-items: center;

        .close-icon {
            cursor: pointer;
        }
    }


    /******** UTILS **********/
    [role="tooltip"] {
        z-index: 9999 !important;
    }


    /******** Media Queries ********/
    @media only screen and (max-width: 768px) {
        body {
            font-size: 15px;
        }

        .appContainer {
            width: 95%;
        }

        .offcanvas {
            width: 100%;

            &-bottom {
                height: 100vh;
            }
        }
    }
`
